import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import {
  User,
  Mail,
  Calendar,
  Shield,
  Settings as SettingsIcon,
  Link as LinkIcon,
  Loader2,
  Check,
  AlertCircle,
  X,
} from 'lucide-react'
import Button from '../components/Button'
import Input from '../components/Input'
import { api } from '../services/api'

function UrlSlugModal({
  isOpen,
  onClose,
  contentType,
  languages,
  slugs,
  onSave,
  isSaving,
}) {
  const [localSlugs, setLocalSlugs] = useState({})

  useEffect(() => {
    if (isOpen) {
      setLocalSlugs(slugs || {})
    }
  }, [isOpen, slugs])

  if (!isOpen) return null

  const handleSave = () => {
    onSave(localSlugs)
  }

  const contentTypeLabels = {
    post: 'Posts',
    page: 'Pages',
    category: 'Categories',
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              {contentTypeLabels[contentType]} URL Slugs
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
            <p className="text-sm text-gray-500">
              Set the URL prefix for{' '}
              {contentTypeLabels[contentType].toLowerCase()} in each language.
            </p>

            {languages.map((lang) => (
              <div key={lang.code} className="flex items-center gap-3">
                <div className="flex items-center gap-2 w-24 shrink-0">
                  <span className="text-lg">{lang.flag}</span>
                  <span className="text-sm font-medium text-gray-700">
                    {lang.code.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center flex-1">
                  <span className="text-gray-400 text-sm mr-1">/</span>
                  <Input
                    value={localSlugs[lang.code] || ''}
                    onChange={(e) =>
                      setLocalSlugs({
                        ...localSlugs,
                        [lang.code]: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, ''),
                      })
                    }
                    placeholder={contentType + 's'}
                    className="flex-1"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-lg">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Settings() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  })

  const isSuperAdmin = user?.roles?.some((r) => r.name === 'superadmin')

  const [modalState, setModalState] = useState({
    isOpen: false,
    contentType: null,
  })
  const [urlSlugError, setUrlSlugError] = useState(null)
  const [urlSlugSuccess, setUrlSlugSuccess] = useState(false)

  const { data: languagesData, isLoading: loadingLanguages } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.get('/languages').then((res) => res.data),
    enabled: isSuperAdmin,
  })

  const languages = Array.isArray(languagesData?.data)
    ? languagesData.data.filter((l) => l.is_active)
    : []

  const { data: urlSlugsData, isLoading: loadingUrlSlugs } = useQuery({
    queryKey: ['url-slugs'],
    queryFn: () => api.get('/settings/url-slugs').then((res) => res.data),
    enabled: isSuperAdmin,
  })

  const urlSlugs = urlSlugsData?.data || {
    post_slugs: {},
    page_slugs: {},
    category_slugs: {},
  }

  const updateSlugsMutation = useMutation({
    mutationFn: (data) => api.put('/settings/url-slugs', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['url-slugs'])
      setModalState({ isOpen: false, contentType: null })
      setUrlSlugSuccess(true)
      setTimeout(() => setUrlSlugSuccess(false), 3000)
    },
    onError: (error) => {
      const message =
        error.response?.data?.message ||
        Object.values(error.response?.data?.errors || {}).flat()[0] ||
        'Failed to save URL slugs'
      setUrlSlugError(message)
      setTimeout(() => setUrlSlugError(null), 5000)
    },
  })

  const handleOpenModal = (contentType) => {
    setModalState({ isOpen: true, contentType })
  }

  const handleSaveModal = (slugs) => {
    const key = `${modalState.contentType}_slugs`
    updateSlugsMutation.mutate({ [key]: slugs })
  }

  const getSlugPreview = (slugs) => {
    const defaultLang = languages.find((l) => l.is_default)
    if (defaultLang && slugs[defaultLang.code]) {
      return `/${slugs[defaultLang.code]}/...`
    }
    const firstSlug = Object.values(slugs || {})[0]
    return firstSlug ? `/${firstSlug}/...` : '/...'
  }

  const handleSave = () => {
    setIsEditing(false)
  }

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
    })
    setIsEditing(false)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings and preferences.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Profile Information
          </h3>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              {isEditing ? (
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="mt-1"
                />
              ) : (
                <div className="mt-1 flex items-center">
                  <User className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">{user?.name}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              {isEditing ? (
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="mt-1"
                />
              ) : (
                <div className="mt-1 flex items-center">
                  <Mail className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">{user?.email}</span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700">
              Account Details
            </label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                Member since: {new Date(user?.created_at).toLocaleDateString()}
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Shield className="h-4 w-4 text-gray-400 mr-2" />
                Role:{' '}
                {user?.roles
                  ?.map((r) => r.name.charAt(0).toUpperCase() + r.name.slice(1))
                  .join(', ') || 'No role assigned'}
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            {isEditing ? (
              <div className="space-x-3">
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>Save Changes</Button>
              </div>
            ) : (
              <Button onClick={() => setIsEditing(true)}>Edit Profile</Button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Admin Settings
          </h3>

          <div className="space-y-4">
            <Link to="/menu-permissions">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="flex items-center">
                  <SettingsIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      Menu Permissions
                    </h4>
                    <p className="text-sm text-gray-500">
                      Configure which menu items are visible to each role
                    </p>
                  </div>
                </div>
                <div className="text-gray-400">→</div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <LinkIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                URL Slugs
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Configure the root URL paths for different content types per
              locale.
            </p>

            {urlSlugError && (
              <div className="flex items-center text-sm text-red-600 bg-red-50 p-3 rounded-md mb-4">
                <AlertCircle className="h-4 w-4 mr-2 shrink-0" />
                {urlSlugError}
              </div>
            )}

            {urlSlugSuccess && (
              <div className="flex items-center text-sm text-green-600 bg-green-50 p-3 rounded-md mb-4">
                <Check className="h-4 w-4 mr-2 shrink-0" />
                URL slugs saved successfully
              </div>
            )}

            {loadingUrlSlugs || loadingLanguages ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-3">
                <div
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleOpenModal('post')}
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold text-sm">
                        P
                      </span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        Posts URL Slug
                      </h4>
                      <p className="text-xs text-gray-500">
                        {getSlugPreview(urlSlugs.post_slugs)}
                      </p>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm">
                    Configure
                  </Button>
                </div>

                <div
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleOpenModal('page')}
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-green-600 font-semibold text-sm">
                        Pg
                      </span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        Pages URL Slug
                      </h4>
                      <p className="text-xs text-gray-500">
                        {getSlugPreview(urlSlugs.page_slugs)}
                      </p>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm">
                    Configure
                  </Button>
                </div>

                <div
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleOpenModal('category')}
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-purple-600 font-semibold text-sm">
                        C
                      </span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        Categories URL Slug
                      </h4>
                      <p className="text-xs text-gray-500">
                        {getSlugPreview(urlSlugs.category_slugs)}
                      </p>
                    </div>
                  </div>
                  <Button variant="secondary" size="sm">
                    Configure
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            System Settings
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Dark Mode</h4>
                <p className="text-sm text-gray-500">Switch to dark theme</p>
              </div>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1" />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Email Notifications
                </h4>
                <p className="text-sm text-gray-500">Receive email updates</p>
              </div>
              <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg border border-red-200">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-red-900 mb-4">
            Danger Zone
          </h3>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                Reset Password
              </h4>
              <p className="text-sm text-gray-500">Send password reset email</p>
              <Button variant="secondary" className="mt-2">
                Send Reset Email
              </Button>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-red-900">
                Delete Account
              </h4>
              <p className="text-sm text-gray-500">
                Permanently delete your account and all data
              </p>
              <Button variant="destructive" className="mt-2">
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>

      <UrlSlugModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, contentType: null })}
        contentType={modalState.contentType}
        languages={languages}
        slugs={urlSlugs[`${modalState.contentType}_slugs`]}
        onSave={handleSaveModal}
        isSaving={updateSlugsMutation.isPending}
      />
    </div>
  )
}
